import React from 'react';
import {
  DeleteIcon,
  DocIcon,
  ImportIcon,
  JpgIcon,
  PdfIcon,
  PenIcon,
  PngIcon,
  XlsxIcon,
  XlxIcon,
} from './icons';
import css from './list-item.module.css';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import DocumentStorageFile from '../../../interfaces/document-storage-file';

const icons = {
  pdf: PdfIcon,
  doc: DocIcon,
  docx: DocIcon,
  xls: XlxIcon,
  xlsx: XlsxIcon,
  jpg: JpgIcon,
  png: PngIcon,
};

export interface IFolderItem {
  name: string;
  path: string;
  created_at: string;
  last_modified: string;
  file_count: number;
  total_size_mb: number;
  deletable: boolean;
}

interface IFolder {
  item: IFolderItem;
  selected?: IFolderItem;
  setSelected?: (item: IFolderItem) => void;
  handleModal?: (type: string) => void;
  showFileUploadModal?: boolean;
  setShowFileUploadModal?: (value: boolean) => void;
  setEditFolder?: (item: IFolderItem) => void;
  canDelete?: boolean;
  canEdit?: boolean;
  canCreate?: boolean;
}

const BASE_CLASS = 'list-item';

export const FolderCard = ({
  item,
  selected,
  setSelected,
  handleModal,
  setShowFileUploadModal,
  setEditFolder,
  canDelete = true,
  canEdit = true,
  canCreate = true,
}: IFolder) => {
  const { t } = useTranslation();

  const handleClickFolder = () => {
    if (item !== selected) {
      setSelected(item);
      setShowFileUploadModal(true);
    } else {
      setSelected(null);
      setShowFileUploadModal(false);
    }
  };

  const handleClickAddFile = (event) => {
    event.stopPropagation();
    setSelected(item);
    setShowFileUploadModal(true);
  };

  const handleEdit = (event, type: string) => {
    event.stopPropagation();
    setEditFolder(item);
    handleModal(type);
  };

  const formattedDate = moment(item.created_at).format('DD.MM.YYYY');

  return (
    <div
      className={`${css[BASE_CLASS]} ${
        item === selected && css[BASE_CLASS + '-highlight']
      }`}
      onClick={handleClickFolder}
    >
      <div className={css[BASE_CLASS + '-top']}>
        <p className={css[BASE_CLASS + '-title']}>
          <span>{item.name}</span>
        </p>
        <div className={css[BASE_CLASS + '-action']}>
          {canCreate && (
            <p
              className={css[BASE_CLASS + '-link']}
              onClick={handleClickAddFile}
            >
              {t('pages.document_storage.add_file')}
            </p>
          )}
          {item.deletable && (
            <>
              {canEdit && (
                <button onClick={(event) => handleEdit(event, 'edit')}>
                  <img src={PenIcon} alt="Pen Icon" />
                </button>
              )}
              {canDelete && (
                <button onClick={(event) => handleEdit(event, 'delete')}>
                  <img src={DeleteIcon} alt="Trash Icon" />
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className={css[BASE_CLASS + '-bottom']}>
        <p>
          {t('pages.document_storage.number_of_files')}: {item.file_count}{' '}
          {t('pages.document_storage.files')}
        </p>
        <p>
          {t('pages.document_storage.total_file_size')}: {item.total_size_mb} MB
        </p>
        {item.deletable && (
          <p>
            {t('pages.document_storage.folder_creation_date')}: {formattedDate}
          </p>
        )}
      </div>
    </div>
  );
};

interface IFile {
  item: DocumentStorageFile;
  download?: (fileName: string) => void;
  handleModal?: (type: string) => void;
  setEditFile: (file: DocumentStorageFile) => void;
  canDelete?: boolean;
  canEdit?: boolean;
}

export const FileCard = ({
  item,
  download,
  handleModal,
  setEditFile,
  canDelete = true,
  canEdit = true,
}: IFile) => {
  const { t } = useTranslation();

  const handleEdit = (type: string) => {
    setEditFile(item);
    handleModal(type);
  };

  const formattedDate = moment(item.last_modified).format('DD.MM.YYYY');

  return (
    <div className={css[BASE_CLASS]}>
      <div className={css[BASE_CLASS + '-top']}>
        <p className={css[BASE_CLASS + '-title']}>
          <img
            src={icons[item.type]}
            alt="File Icon"
            className={css[BASE_CLASS + 'title-icon']}
          />
          <span>{item.name}</span>
        </p>
        <div className={css[BASE_CLASS + '-action']}>
          <button onClick={() => download(item.name)}>
            <img src={ImportIcon} alt="Import Icon" />
          </button>
          {canEdit && (
            <button onClick={() => handleEdit('edit')}>
              <img src={PenIcon} alt="Pen Icon" />
            </button>
          )}
          {canDelete && (
            <button onClick={() => handleEdit('delete')}>
              <img src={DeleteIcon} alt="Trash Icon" />
            </button>
          )}
        </div>
      </div>
      <div className={css[BASE_CLASS + '-bottom']}>
        <p>
          {t('pages.document_storage.file_size')}: {item.size} MB
        </p>
        <p>
          {t('pages.document_storage.file_format')}:{' '}
          <span className={css['format-text']}>{item.type}</span>
        </p>
        <p>
          {t('pages.document_storage.file_creation_date')}: {formattedDate}
        </p>
      </div>
    </div>
  );
};
